import React, { useState } from 'react'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { dashboardPageContainerStyle } from '@/constants/layout'
import { SubmitHandler, useForm } from 'react-hook-form'
import { FormFooter } from '@/components/organisms/settings/mail-delivery/form-footer'
import { DateExecution, DeliveryMethod, MailDeliveryContent, MailDeliveryContentInput } from '@/models/mail-delivery/mail-delivery-content'
import * as api from '@/apis/aipass'
import { errorHandler } from '@/libs/errors'
import { useHistory, useParams } from 'react-router-dom'
import { FormMailContent } from '@/components/organisms/settings/mail-delivery/form-mail-content'
import { defaultValues, FormDeliveryCondition } from '@/components/organisms/settings/mail-delivery/form-delivery-condition'
import { useSettingsEditPageGuardEffect } from '@/hooks/use-settings-edit-guard'

export const MailDeliveryContentEdit: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState<MailDeliveryContent>()
  const { t } = useTranslation()
  const history = useHistory()
  const { id } = useParams<{ id: string }>()
  const useFormReturn = useForm<MailDeliveryContentInput>({ defaultValues })

  const onSubmit: SubmitHandler<MailDeliveryContentInput> = async submitData => {
    try {
      setLoading(true)
      await api.updateMailDeliveryContent(id, submitData)
      return history.push({ pathname: `/setting/mail-delivery` })
    } catch (error) {
      errorHandler(error)
    } finally {
      setLoading(false)
    }
  }

  const getMailDeliveryContentDetail = async () => {
    try {
      setLoading(true)
      const detail = await api.getMailDeliveryContentDetail(id)
      const channelCodes = detail.channelCodes.map(channelCode => channelCode.reservationChannelCodeId)
      useFormReturn.reset({
        title: detail.title.ja,
        status: detail.status,
        deliveryDate: {
          numberOfDay: detail.deliveryDate.numberOfDay,
          dateExecution: detail.deliveryDate.dateExecution || DateExecution.Ago,
          deliveryTime: detail.deliveryDate.deliveryTime,
          targetStatus: detail.deliveryDate.targetStatus,
        },
        isSendToAfterDelivery: detail.isSendToAfterDelivery,
        lineNotificationStatus: detail.lineNotificationStatus,
        stayTypeTarget: detail.stayTypeTarget,
        reservationStatuses: detail.reservationStatuses,
        channelCodes: detail.isNullSearchableChannelCode ? ['-', ...channelCodes] : channelCodes,
        content: detail.content.ja,
        rawContent: detail.rawContent.ja,
      })
      setDetail(detail)
    } catch (error) {
      errorHandler(error)
    } finally {
      setLoading(false)
    }
  }

  useSettingsEditPageGuardEffect({
    redirectPath: '/setting/mail-delivery',
    onAllowed: getMailDeliveryContentDetail,
  })

  return detail ? (
    <SettingsLayout
      loading={loading}
      footerContent={
        <FormFooter
          method={DeliveryMethod.Mail}
          onSave={useFormReturn.handleSubmit(onSubmit)}
          onCancel={() => history.push({ pathname: `/setting/mail-delivery` })}
          useFormReturn={useFormReturn}
        />
      }
    >
      <div css={dashboardPageContainerStyle}>
        <div css={headerSettingStaffStyle}>
          <div css={headerDetailStyle}>{t('Delivery Details')}</div>
        </div>
        <FormMailContent useFormReturn={useFormReturn} detail={detail} />
        <FormDeliveryCondition useFormReturn={useFormReturn} />
      </div>
    </SettingsLayout>
  ) : (
    <></>
  )
}

const headerSettingStaffStyle = css({
  height: '50px',
  padding: '0 32px',
  borderBottom: '1px solid #F2F2F2',
  display: 'flex',
  justifyContent: 'space-between',
})

const headerDetailStyle = css({
  fontSize: '18px',
  fontWeight: 'bold',
  padding: '16px 0',
  color: '#272727',
})
